import React from 'react'
import { Link } from 'react-router-dom'
import {
  COLUMN_TYPES,
  DATA_TYPES,
  SEARCH_TYPES,
  constants,
  coreConstants,
  Icon,
  reducers,
  Popup,
} from '@adc/ui-morpheus'
import { USER_LISTINGS_BANNED_FROM_BIDDING } from '../../constants/tableTypes'

const {
  actionCellActionTypes: { ACTION_CELL_ACTION_TYPES },
  optionLists: { globalPhysicalStructures },
} = constants

const { states: statesList, listingStatus: listingStatusList } =
  coreConstants.optionLists

const defaultColProps = { isResizable: true, isVisible: true }
const defaultColPropsWithWidth200 = { width: 200, ...defaultColProps }
const defaultColPropsWithWidth100 = { width: 100, ...defaultColProps }
export const tableConfig = {
  config: {
    entityKey: USER_LISTINGS_BANNED_FROM_BIDDING,
    primaryKeyField: 'listing_id',
  },
  fieldDefinitions: [
    {
      apiRef: 'listing_id',
      name: 'Listing ID',
      formatOptions: { custom: 'listingIdFormatter' },
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.property_id',
      name: 'Property ID',
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'banned.created_date',
      name: 'User Banned On',
      dataType: DATA_TYPES.DATETIME_AS_LOCAL,
      searchType: SEARCH_TYPES.DATE,
      columnDefinition: {
        type: COLUMN_TYPES.DATE,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'seller_property.street_description',
      name: 'Address',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'seller_property.municipality',
      name: 'City',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'seller_property.country_primary_subdivision',
      name: 'State',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: statesList,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'seller_property.postal_code',
      name: 'Zip',
      columnDefinition: {
        partialMatch: true,
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'seller_property.country_secondary_subdivision',
      name: 'County',
      searchType: SEARCH_TYPES.SUGGEST_ASYNC,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'listing_status',
      name: 'Listing Status',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: listingStatusList,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      apiRef: 'primary_property.summary.total_bedrooms',
      name: 'Bedrooms',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.total_bathrooms',
      name: 'Bathrooms',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.living_size',
      name: 'Square Footage',
      dataType: DATA_TYPES.NUMBER_RANGE,
      columnDefinition: {
        type: COLUMN_TYPES.TEXT,
        colProps: defaultColPropsWithWidth100,
      },
    },
    {
      apiRef: 'primary_property.summary.structure_type_code',
      name: 'Structure Type',
      searchType: SEARCH_TYPES.SUGGEST_SYNC,
      options: globalPhysicalStructures,
      columnDefinition: {
        type: COLUMN_TYPES.VALUE_TEXT,
        colProps: defaultColPropsWithWidth200,
      },
    },
    {
      name: 'View',
      apiRef: '__actions__',
      isUiOnly: true,
      columnDefinition: {
        actions: [
          ACTION_CELL_ACTION_TYPES.EDIT_DETAILS,
          ACTION_CELL_ACTION_TYPES.REMOVE_ROW,
        ],
        type: COLUMN_TYPES.ACTION,
        colProps: { width: 100, fixedRight: true, align: 'center' },
      },
    },
  ],
  filterClauses: [],
  sortBy: [
    {
      field: 'auction.auction_date',
      order: 'desc',
    },
  ],
}

const linkColumnAction = (actionIndex, rowIndex, primaryId) => (
  <Link
    style={{ margin: -8, padding: 8 }}
    key={actionIndex}
    to={`/property/listings/${primaryId}/view`}
    data-elm-id={`action_edit_${rowIndex}`}
    target='_blank'
    rel='noopener noreferrer'
  >
    <Icon size='large' style={{ marginTop: -3 }} name='eye' color='info' />
  </Link>
)

const unbanRow = (
  actionIndex,
  rowIndex,
  primaryId,
  data,
  onRowAction,
  userAuthorities
) => {
  return (
    <Popup
      content='Unban User from Listing/Property'
      key={actionIndex}
      size='tiny'
      trigger={
        <Icon
          link
          color='error'
          size='large'
          name='delete'
          style={{ marginTop: -3 }}
          data-elm-id={`user_banned_listings_action_remove_${rowIndex}`}
          onClick={() => {
            onRowAction(primaryId)
          }}
        />
      }
    />
  )
}

const columnAction = actionCellActionType => {
  switch (actionCellActionType) {
    case ACTION_CELL_ACTION_TYPES.EDIT_DETAILS:
      return linkColumnAction
    case ACTION_CELL_ACTION_TYPES.REMOVE_ROW:
      return unbanRow
  }
}

/**
 *
 * @param {*} fieldDefinitions
 * THIS SELECTOR IS TO MAINTAIN BACKWARDS COMPATIBILITY WITH EVERYTHING IN TOOLKIT
 * It will change/ be removed when everything there changes. This is so we can refactor slowly
 */
export const columnSelector = (state, userAuthorities, suggestions) =>
  state.fieldDefinitions
    .filter(field =>
      field.requiredPermissions
        ? JSON.parse(field.requiredPermissions).every(authority =>
            userAuthorities.includes(authority)
          )
        : true
    )
    .filter(field => field.columnDefinition)
    .map(col => {
      const column = {
        ...col.columnDefinition,
        ...{ field: col.apiRef, title: col.name },
      }
      if (column.actions) column.actions = column.actions.map(columnAction)
      if (col.dataType) column.dataType = col.dataType
      if (col.searchType) column.searchType = col.searchType
      if (col.options) column.options = col.options
      if (col.formatOptions) column.formatOptions = col.formatOptions
      return column
    })

export default reducers.dataTable.dataTable(
  USER_LISTINGS_BANNED_FROM_BIDDING,
  tableConfig
)
